import { api } from '../components/role-play/apiService';

/**
 * Organization join requests.
 *
 * Two audiences, matching the backend's two routers: an applicant reading and re-sending
 * their own request (`/join-requests/*`), and a Trainer working through their org's
 * queue (`/organization/{id}/*`).
 */

export type JoinRequestStatus =
  | 'pending'
  | 'approved_trial'
  | 'locked_out'
  | 'seated'
  | 'rejected'
  | 'lapsed';

/** The three-way Trainer decision. `seated` is the "Accept" option. */
export type JoinRequestDecision = 'rejected' | 'approved_trial' | 'seated';

export interface JoinRequest {
  id: string;
  applicant_email: string;
  applicant_user_id: string;
  organization_id: string;
  status: JoinRequestStatus;
  created_at: string;
  expires_at: string;
  decided_at?: string | null;
  decided_by?: string | null;
  seated_at?: string | null;
  /** Both resolved from Keycloak; either can be null if the account has since gone. */
  decided_by_name?: string | null;
  applicant_name?: string | null;
}

export interface JoinRequestList {
  pending: JoinRequest[];
  /** approved_trial and locked_out together — everyone waiting on the one manual step. */
  awaiting_seat: JoinRequest[];
  history: JoinRequest[];
  /**
   * Null means "couldn't determine", not "none free" — the org's live member count was
   * unavailable, or it has no seat limit set. Accept and Move-into-seat are hidden in
   * that case rather than offering an action the server would then refuse.
   */
  free_seats: number | null;
  seat_limit: number | null;
  /** Trial-seat pool usage — separate from free_seats/seat_limit above; Approve with
   * Trial draws from this pool instead of the org's real seats. */
  trial_seats_used: number;
  trial_seat_limit: number;
  /** False for a Trainer without approval rights: they see the list, read-only. */
  can_approve: boolean;
}

/** What an applicant is allowed to know about their own request. Empty for the vast
 *  majority of users, who have never made one. */
export interface MyJoinRequest {
  status?: JoinRequestStatus | null;
  organization_id?: string | null;
  organization_name?: string | null;
  created_at?: string | null;
  expires_at?: string | null;
  decided_at?: string | null;
  /** Set only while a rejected/lapsed request is still inside its 7-day cooldown. */
  can_re_request_at?: string | null;
  can_re_request: boolean;
  /** Who to contact about a seat — set only for approved_trial/locked_out. */
  billing_owner_name?: string | null;
  billing_owner_email?: string | null;
}

export interface JoinSettings {
  organization_id: string;
  domain?: string | null;
  auto_approve_join_requests: boolean;
  default_trial_seats: number;
  billing_owner_id?: string | null;
  billing_owner_name?: string | null;
  billing_owner_email?: string | null;
  /** False for Marketplace orgs — their billing lives in Google Cloud and can't be handed over here. */
  can_transfer_billing_owner?: boolean;
}

export interface JoinSettingsInput {
  /** Empty string clears the claim and stops domain matching for this org. */
  domain?: string | null;
  auto_approve_join_requests?: boolean;
  /** Super admin only — the API returns 403 for anyone else. */
  default_trial_seats?: number;
}

export interface Approver {
  user_id: string;
  name: string;
  email: string;
  role: string;
  can_approve: boolean;
  is_billing_owner: boolean;
}

// ── Applicant ──────────────────────────────────────────────────────────────

export async function fetchMyJoinRequest(): Promise<MyJoinRequest> {
  return api.get<MyJoinRequest>('/join-requests/me');
}

/** Re-request after a rejection or a lapse. The org is never sent — the server always
 *  resolves it from the caller's own email domain. */
export async function createJoinRequest(): Promise<MyJoinRequest> {
  return api.post<MyJoinRequest>('/join-requests', {});
}

// ── Trainer ────────────────────────────────────────────────────────────────

export async function fetchJoinRequests(orgId: string): Promise<JoinRequestList> {
  return api.get<JoinRequestList>(`/organization/${orgId}/join-requests`);
}

export async function decideJoinRequest(
  requestId: string,
  decision: JoinRequestDecision
): Promise<JoinRequest> {
  return api.post<JoinRequest>(`/join-requests/${requestId}/decide`, { decision });
}

export async function assignSeat(requestId: string): Promise<JoinRequest> {
  return api.post<JoinRequest>(`/join-requests/${requestId}/seat`, {});
}

// ── Org settings ───────────────────────────────────────────────────────────

export async function fetchJoinSettings(orgId: string): Promise<JoinSettings> {
  return api.get<JoinSettings>(`/organization/${orgId}/join-settings`);
}

export async function updateJoinSettings(
  orgId: string,
  input: JoinSettingsInput
): Promise<JoinSettings> {
  return api.patch<JoinSettings>(`/organization/${orgId}/join-settings`, input);
}

export async function transferBillingOwner(orgId: string, userId: string): Promise<JoinSettings> {
  return api.patch<JoinSettings>(`/organization/${orgId}/billing-owner`, { user_id: userId });
}

export async function fetchApprovers(orgId: string): Promise<Approver[]> {
  return api.get<Approver[]>(`/organization/${orgId}/approvers`);
}

export async function setApprovalRights(
  orgId: string,
  userId: string,
  canApprove: boolean
): Promise<Approver> {
  return api.patch<Approver>(`/organization/${orgId}/approvers/${userId}`, {
    can_approve: canApprove,
  });
}
